import React, { useMemo } from "react";
import { Source, Layer } from "react-map-gl/mapbox";
import * as turf from "@turf/turf";

const FIRE_BUFFER_SOURCE_ID = "fire-buffer-source";
const FIRE_BUFFER_FILL_ID = "fire-buffer-fill";
const FIRE_BUFFER_OUTLINE_ID = "fire-buffer-outline";

const isPolygonGeometry = (geometry) =>
  geometry?.type === "Polygon" || geometry?.type === "MultiPolygon";

// Fire risk for area assessments is computed over a buffer around each polygon.
// Single-polygon assessments carry `buffer_polygon` on `description_dict`;
// multi-polygon assessments carry one per `polygon_results` entry. Overlapping
// buffers are merged into one shape so they don't stack/darken and there is no
// ambiguity about which buffer is which — the union is the assessed fire area.
// Returns a GeoJSON FeatureCollection with a single feature, or null.
export const getFireBufferFeatureCollection = (fire) => {
  const dict = fire?.description_dict;
  if (!dict) return null;

  const results = Array.isArray(dict.polygon_results)
    ? dict.polygon_results
    : [];
  const geometries = results.length
    ? results.map((result) => result?.buffer_polygon)
    : [dict.buffer_polygon];

  const features = geometries
    .filter(isPolygonGeometry)
    .map((geometry) => ({ type: "Feature", properties: {}, geometry }));

  if (!features.length) return null;

  const source = { type: "FeatureCollection", features };
  if (features.length === 1) return source;

  try {
    const merged = turf.union(source);
    if (merged) return { type: "FeatureCollection", features: [merged] };
  } catch {
    // Fall back to the un-merged buffers if the union fails.
  }
  return source;
};

/**
 * Renders the fire assessment buffer as a single merged polygon on the map,
 * styled to match the point-assessment fire radius circle (circleSource.js).
 * Only meaningful for fire; the parent gates rendering on the active hazard.
 */
export default function FireBufferPolygons({ fire }) {
  const data = useMemo(() => getFireBufferFeatureCollection(fire), [fire]);

  if (!data) return null;

  return (
    <Source id={FIRE_BUFFER_SOURCE_ID} type="geojson" data={data}>
      <Layer
        id={FIRE_BUFFER_FILL_ID}
        type="fill"
        paint={{ "fill-color": "black", "fill-opacity": 0.15 }}
      />
      <Layer
        id={FIRE_BUFFER_OUTLINE_ID}
        type="line"
        layout={{ "line-cap": "round", "line-join": "round" }}
        paint={{
          "line-color": "#4eb3f2",
          "line-width": 3,
          // Keep the outline visible even where 3D buildings/terrain occlude it.
          "line-occlusion-opacity": 1,
        }}
      />
    </Source>
  );
}
